<script setup>
import { computed, ref, shallowRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Textarea } from '@drama/ui'
import FormField from './NovelFormField.vue'
import { cloneJson, createRequestId } from '@drama/novel-authoring'
import { adoptOpeningProposal, createOpeningProposal, openingProposalIsCurrent } from '@drama/novel-authoring'

const props = defineProps({
  modelValue: { type: Object, required: true },
  assist_opening: { type: Function, required: true },
  disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()
const action = ref('')
const instructions = ref('')
const pending = ref(false)
const error_key = ref('')
const proposal = shallowRef(null)
const adopted = ref(false)
const has_opening = computed(() => (props.modelValue.opening?.segments ?? []).some(segment => segment.text?.trim()))
const stale = computed(() => proposal.value !== null && !openingProposalIsCurrent(props.modelValue, proposal.value))
const submit_disabled = computed(() => props.disabled || pending.value || (action.value === 'revise' && !instructions.value.trim()))

function chooseAction(next) {
  action.value = next
  error_key.value = ''
  adopted.value = false
}

function errorKey(error) {
  const codes = [error?.code, error?.message]
  if (codes.includes('opening_assist_context_required')) return 'contextRequired'
  if (codes.includes('opening_assist_context_too_large')) return 'contextTooLarge'
  if (codes.includes('opening_assist_opening_required')) return 'openingRequired'
  if (codes.includes('opening_assist_instructions_required')) return 'instructionsRequired'
  if (codes.includes('opening_assist_unavailable')) return 'unavailable'
  if (codes.includes('opening_assist_response_invalid')) return 'invalidResponse'
  if (codes.includes('opening_assist_busy')) return 'busy'
  if (codes.includes('content_conflict')) return 'contentConflict'
  if (codes.includes('revision_conflict') || codes.includes('request_id_conflict') || error?.status === 409) return 'conflict'
  return 'failed'
}

async function generate() {
  if (submit_disabled.value) return
  const source = cloneJson(props.modelValue)
  const selected_action = action.value
  pending.value = true
  error_key.value = ''
  adopted.value = false
  try {
    const result = await props.assist_opening({
      pack: source,
      action: selected_action,
      instructions: instructions.value.trim(),
      request_id: createRequestId(),
    })
    proposal.value = createOpeningProposal(source, result.opening)
  } catch (error) {
    error_key.value = errorKey(error)
  } finally {
    pending.value = false
  }
}

function adopt() {
  if (props.disabled || pending.value || stale.value || !proposal.value) return
  emit('update:modelValue', adoptOpeningProposal(props.modelValue, proposal.value))
  proposal.value = null
  action.value = ''
  instructions.value = ''
  adopted.value = true
}

function discard() {
  proposal.value = null
  error_key.value = ''
}

function speakerName(speaker_id) {
  if (speaker_id === 'narrator') return t('novelStudio.fields.narrator')
  return props.modelValue.characters?.find(person => person.id === speaker_id)?.name || speaker_id
}
</script>

<template>
  <section
    class="opening-assistant"
    :aria-label="t('novelStudio.openingAi.title')"
    data-testid="novel-opening-assistant"
  >
    <header class="opening-assistant__header">
      <div>
        <h3>{{ t('novelStudio.openingAi.title') }}</h3>
        <p>{{ t('novelStudio.openingAi.hint') }}</p>
      </div>
      <div
        class="opening-assistant__actions"
        role="group"
        :aria-label="t('novelStudio.openingAi.title')"
      >
        <Button
          variant="secondary"
          size="sm"
          :disabled="disabled || pending"
          :aria-pressed="action === 'draft'"
          data-testid="novel-opening-ai-draft"
          @click="chooseAction('draft')"
        >
          {{ t('novelStudio.openingAi.draft') }}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          :disabled="disabled || pending || !has_opening"
          :aria-pressed="action === 'revise'"
          data-testid="novel-opening-ai-revise"
          @click="chooseAction('revise')"
        >
          {{ t('novelStudio.openingAi.revise') }}
        </Button>
      </div>
    </header>
    <form
      v-if="action"
      class="opening-assistant__form"
      :aria-busy="pending"
      @submit.prevent="generate"
    >
      <FormField
        :required="action === 'revise'"
        :label="t('novelStudio.openingAi.instructions')"
        :hint="t(`novelStudio.openingAi.${action}Help`)"
      >
        <Textarea
          v-model="instructions"
          :rows="3"
          :maxlength="4000"
          :disabled="pending"
          :placeholder="t(`novelStudio.openingAi.${action}Placeholder`)"
          data-testid="novel-opening-ai-instructions"
        />
      </FormField>
      <div class="opening-assistant__actions">
        <Button
          type="submit"
          size="sm"
          :disabled="submit_disabled"
          :loading="pending"
          data-testid="novel-opening-ai-generate"
        >
          {{ t(pending ? 'novelStudio.openingAi.generating' : proposal ? 'novelStudio.openingAi.regenerate' : 'novelStudio.openingAi.generate') }}
        </Button>
        <Button
          v-if="!pending"
          variant="ghost"
          size="sm"
          @click="action = ''"
        >
          {{ t('novelStudio.editor.close') }}
        </Button>
      </div>
      <p
        v-if="pending"
        role="status"
      >
        {{ t('novelStudio.openingAi.pending') }}
      </p>
    </form>
    <p
      v-if="error_key"
      class="opening-assistant__error"
      role="alert"
    >
      {{ t(`novelStudio.openingAi.errors.${error_key}`) }}
    </p>
    <section
      v-if="proposal"
      class="opening-assistant__preview"
      :aria-label="t('novelStudio.openingAi.preview')"
      data-testid="novel-opening-ai-preview"
    >
      <h4>{{ t('novelStudio.openingAi.preview') }}</h4>
      <article
        v-for="(segment, index) in proposal.opening.segments"
        :key="index"
        class="opening-assistant__paragraph"
      >
        <p class="opening-assistant__speaker">
          {{ speakerName(segment.speaker_id) }} · {{ t(`novelStudio.channels.${segment.channel}`) }}
        </p>
        <p class="opening-assistant__text">
          {{ segment.text }}
        </p>
      </article>
      <p
        v-if="stale"
        class="opening-assistant__error"
        role="alert"
      >
        {{ t('novelStudio.openingAi.stale') }}
      </p>
      <div class="opening-assistant__actions">
        <Button
          size="sm"
          :disabled="disabled || pending || stale"
          data-testid="novel-opening-ai-adopt"
          @click="adopt"
        >
          {{ t('novelStudio.openingAi.adopt') }}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          :disabled="pending"
          data-testid="novel-opening-ai-discard"
          @click="discard"
        >
          {{ t('novelStudio.openingAi.discard') }}
        </Button>
      </div>
    </section>
    <p
      v-if="adopted"
      role="status"
    >
      {{ t('novelStudio.openingAi.adopted') }}
    </p>
  </section>
</template>

<style scoped>
.opening-assistant {
  display: grid;
  gap: var(--drama-space-md);
  padding: var(--drama-space-lg);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface);
}
.opening-assistant__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--drama-space-md);
}
.opening-assistant h3, .opening-assistant h4, .opening-assistant p { margin: 0; }
.opening-assistant h3, .opening-assistant h4 { font-size: var(--drama-font-size-md); color: var(--drama-color-text); }
.opening-assistant p { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); line-height: 1.6; }
.opening-assistant__header p { margin-top: var(--drama-space-xs); }
.opening-assistant__actions { display: flex; gap: var(--drama-space-sm); flex-wrap: wrap; }
.opening-assistant__form, .opening-assistant__preview { display: grid; gap: var(--drama-space-md); }
.opening-assistant__preview { padding-top: var(--drama-space-lg); border-top: 1px solid var(--drama-color-border); }
.opening-assistant__paragraph { display: grid; gap: var(--drama-space-xs); }
.opening-assistant .opening-assistant__speaker { font-size: var(--drama-font-size-xs); }
.opening-assistant .opening-assistant__text { color: var(--drama-color-text); white-space: pre-wrap; }
.opening-assistant .opening-assistant__error { color: var(--drama-color-danger); }
</style>
